import { createAction, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { createAppAsyncThunk } from 'reduxTyped'
import { l } from '@manychat/manyui'

import { localStorage } from 'utils'
import { Body, SupportedChannelsType } from 'apps/chat/actions/messagesActionsTyped/types'
import { LiveChatHideAutomationKey } from 'apps/chat/constants/LiveChatHideAutomationsKey'
import { ChatMessageBlockType } from 'apps/chat/types/chatMessagesBlockList'
import { billing, UpgradeSource } from 'common/billing'
import { PRODUCT_NAME_MAP } from 'common/billing/constants'
import { getInboxData } from 'common/billing/entities/addOns/selectors'
import { alert } from 'common/core'
import { ChannelType } from 'common/core/constants/ChannelType'
import { IAsyncThunkAction, IThunkAction } from 'common/core/interfaces/actions'
import { WebSocketEvents } from 'constants/WebSockets'
import { mapResponse } from 'shared/api/lib/mapResponse'
import { chatApi, SendFunction } from 'shared/api/requests/chat'
import { chatApiGuard } from 'shared/api/requests/chat/guards'
import {
  CreateNoteResponse,
  SendMessageResponse,
  Message,
  OutgoingMessage,
  IncomingMessage,
} from 'shared/api/requests/chat/schemas'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'
import { createAsyncAction } from 'shared/lib/redux'
import { mergeData } from 'utils/factory'
import { analyticsService } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'
import { getFirstLiveChatThreadOnboarding } from 'utils/services/newOnboardingService'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

import {
  processSendMessageError,
  resetImages,
  setThreadsError,
  setThreadsResponse,
} from '../actions/threadsActions'
import { IN_TYPES } from '../components/ChatMessage/constants'
import { WhatsAppMessageFailedErrorCodes } from '../constants/chatConstants'
import {
  ThreadControlErrorType,
  HandoverErrorType,
  MAX_NUMBER_OF_HANDOVER_RETRIES,
} from '../constants/LiveChatInterfaces'
import { isSomeMessageSending } from '../selectors/messageSelectors'
import { getCurrentId } from '../selectors/threadSelectors'

const NAMESPACE = 'messagesSlice'

interface MessageCreatedData {
  model?: OutgoingMessage
}

interface MessageReceivedData {
  model?: IncomingMessage
}

interface Options {
  isSendImages?: boolean
  isNeedDelay?: boolean
  isNote?: boolean
}

type Notification = Message

export const notificationsQueue: Notification[] = []

const processSocketSentEvent = createAction<OutgoingMessage>(`${NAMESPACE}/processSocketSentEvent`)

const processSocketReceivedEvent = createAction<IncomingMessage>(
  `${NAMESPACE}/processSocketReceivedEvent`,
)

const updateMessages = createAction<{
  data: SendMessageResponse['messages'] | CreateNoteResponse['messages']
  messageId: string
}>(`${NAMESPACE}/updateMessages`)

export const sendMessage = createAsyncAction(
  `${NAMESPACE}/send`,
  async (
    {
      body,
      options,
      message,
      channel,
    }: {
      channel: SupportedChannelsType
      body: Body
      message: Message
      options: Options
    },
    { dispatch },
  ) => {
    const { isNote } = options

    const params = {
      body,
      ...(channel === ChannelType.WHATSAPP && { query: { clear_question_context: 'true' } }),
    }

    const response =
      isNote && 'channel' in body
        ? await chatApi.messages.createNote({ body })
        : await SendFunction[channel](params)

    dispatch(updateMessages({ data: response.data.messages, messageId: message.message_id }))
    dispatch(processNotificationsQueue())

    return response.data.messages
  },
  {
    onError: (error, { message, channel }, { dispatch }) => {
      if (axios.isAxiosError(error)) {
        // Check if it was a request error
        if (!error.response) {
          alert(error.message, 'danger')
          dispatch(
            setThreadsError({
              currentId: message.user_id,
              channel,
              error: { errors: [error.message] },
            }),
          )
          return true
        }
      }
      return
    },
    onBusinessError: (
      error,
      { channel, body, message, options },
      { dispatch, getState, rejectWithValue },
    ) => {
      error.handle()
      const state = getState()

      let shouldLogError = true

      const processHandoverError = (
        counter: number,
        type: ThreadControlErrorType | HandoverErrorType,
      ) => {
        if (counter && counter < MAX_NUMBER_OF_HANDOVER_RETRIES) {
          dispatch(sendGeneratedMessage(message, body, options, channel))
          analyticsService.sendEvent('LIVE_CHAT.HANDOVER.SEND_MESSAGE.SECOND_ATTEMPT')
        } else {
          analyticsService.sendEvent('LIVE_CHAT.HANDOVER.SEND_MESSAGE.SECOND_ATTEMPT_FAIL')
          if (type === ThreadControlErrorType.HANDOVER_PRIMARY_THREAD_OWNERSHIP) {
            alert(
              l.translate(
                'Something is wrong with Handover Protocol. Please try again or contact Manychat support.',
              ),
              'danger',
            )
          }
        }
      }

      // TODO: Check with BE indicates that chatApiGuard.isAddLinkToDevLog is not required anymore

      // TODO: Only doing this to support error handling in existing threads reducer
      if (chatApiGuard.isHopError(error) || chatApiGuard.isThreadControlError(error)) {
        dispatch(processSendMessageError({ error }))
      }

      // For backward compatibility
      if (chatApiGuard.isHopError(error)) {
        const { counter, type } = error.error_data.hop_error
        processHandoverError(counter, type)
        return true
      }

      if (chatApiGuard.isThreadControlError(error)) {
        const { counter, type } = error.error_data.thread_control_error
        if (
          [
            ThreadControlErrorType.HANDOVER_PRIMARY_THREAD_OWNERSHIP,
            ThreadControlErrorType.HANDOVER_SECONDARY_THREAD_OWNERSHIP,
          ].includes(type)
        ) {
          processHandoverError(counter, type)
          return true
        } else if (
          // Not processing error in FE but it is being logged in the BE to understand them better
          [
            ThreadControlErrorType.CR_UNKNOWN,
            ThreadControlErrorType.CR_HUMAN_TAG_UNAVAILABLE,
          ].includes(type)
        ) {
          shouldLogError = false
        }
      }

      if (billing.isProError(error)) {
        const products = error.error_data.products ?? []
        let message = l.translate(
          "You aren't able to activate this feature on the current plan. Upgrade for unlimited access.",
        )

        if (getInboxData(state).isAvailable) {
          const proProductLabel =
            products?.length > 1
              ? 'Marketing Automation Pro ' + l.translate('or') + ' Inbox Pro'
              : `${PRODUCT_NAME_MAP[products[0]]} Pro`

          message = l.translate(`{product} subscription required to perform this action`, {
            product: proProductLabel,
          })
        }

        billing.handleProError(error, {
          message,
          source: UpgradeSource.WA_UPGRADE_TO_PRO,
        })

        return true
      }

      if (shouldLogError) {
        errorTrackingService.logError(error, {
          scope: 'livechat',
          section: 'message',
          fingerprint: 'send-message',
        })
      }
      alert(error.message, 'danger')

      return rejectWithValue()
    },
    autoHandleByGuards: [
      // These errors are logged in the BE and need to be investigated there. Nothing can be done in FE.
      SendFunction[ChannelType.SMS].isSmsSendMessageFailed,
      SendFunction[ChannelType.FB].isFacebookSendMessageFailed,
      SendFunction[ChannelType.INSTAGRAM].isInstagramSendMessageFailed,
      SendFunction[ChannelType.WHATSAPP].isWhatsAppSendMessageFailed,
      SendFunction[ChannelType.TELEGRAM].isTelegramSendMessageFailed,
      SendFunction[ChannelType.TIKTOK].isTiktokSendMessageFailed,
    ],
  },
)

export const sendGeneratedMessage =
  (
    message: Message,
    body: Body,
    options: Options,
    channel: SupportedChannelsType,
  ): IAsyncThunkAction =>
  async (dispatch) => {
    const { isSendImages, isNeedDelay } = options
    const currentId = message.user_id
    const messageId = message.message_id

    const send = async () => {
      const result = await dispatch(sendMessage({ channel, body, message, options }))
      if ('error' in result) {
        const errorMessage = result?.payload?.message
          ? result.payload.message
          : result.error.message || ''

        dispatch(
          setThreadsError({
            currentId: message.user_id,
            channel,
            error: { errors: [errorMessage] },
          }),
        )
        return result
      }

      if (isSendImages) {
        dispatch(resetImages({ currentId, channel }))
      }

      dispatch(setThreadsResponse({ currentId, channel, messageId }))
      getFirstLiveChatThreadOnboarding()?.steps?.STEP_3?.process?.()

      return result
    }

    // this is necessary because the telegram does not support frequent sending of messages and channel is need pause between messages
    const delay = (action: typeof send) => setTimeout(action, 2000)

    try {
      const sendActionPromise = isNeedDelay ? delay(send) : send()
      return await sendActionPromise
    } catch (error) {
      return
    }
  }

const getInitialHideAutomation = () => {
  try {
    const rawValue = localStorage.getItem(LiveChatHideAutomationKey)
    if (rawValue) {
      return rawValue === 'true'
    }
  } catch {
    // do nothing and return fallback
  }

  return false
}

export const messagesSlice = createListSlice({
  storePath: 'chat.messages',

  namespace: NAMESPACE,
  listKey: 'messages',

  operations: createListOperations({
    getList: mapResponse(chatApi.messages.fetch, (response) => response.data),
    getNextList: mapResponse(chatApi.messages.fetch, (response) => response.data),
    getPrevList: mapResponse(chatApi.messages.fetch, (response) => response.data),
    createItem: undefined,
    deleteItem: undefined,
    updateItem: undefined,
  }),

  getItemId: (item: Message) => item.message_id,

  extraState: {
    hideAutomation: getInitialHideAutomation(),
    scrollDisabled: false,
  },
  reducers: {
    toggleHideAutomation: (state) => {
      state.hideAutomation = !state.hideAutomation
    },
    toggleScrollDisability: (state, action: PayloadAction<boolean>) => {
      state.scrollDisabled = action.payload
    },
    setDeliveryFailed: (
      state,
      action: PayloadAction<{ error_code: WhatsAppMessageFailedErrorCodes; _mid: string }>,
    ) => {
      const { _mid, error_code } = action.payload
      const items = state.items || []

      // This action processes a WS event for failed deliveries on WA. The exact message that is failed is indicated by the _mid field which is a unique
      // identifier for a message in the model.messages array. We have to loop through all the messages and then the model.messages array to find the exact message and update the error_code field.
      loop: for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const model = item.model
        if (!model || !('messages' in model)) {
          continue
        }

        const messages = model?.messages || []

        for (let j = 0; j < messages.length; j++) {
          const message = messages[j]
          if ('type' in message && message.type !== ChatMessageBlockType.TEMPLATE) {
            continue
          }
          if ('_mid' in message && message._mid === _mid) {
            message.error_code = error_code
            break loop
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Handle sending message
    builder.addCase(sendMessage.pending, (state, action) => {
      const { message } = action.meta.arg

      const index = state.items.findIndex((item) => item.message_id === message.message_id)
      if (index >= 0) {
        return
      }

      const newMessage = {
        ...message,
        sending: true,
        message_id: message.message_id,
        id: message.message_id,
      }
      state.items = [newMessage, ...state.items]
      state.byId[message.message_id] = newMessage
    })

    // Handle updating messages
    builder.addCase(updateMessages, (state, action) => {
      const { data, messageId } = action.payload

      const messageFromApi = data[0]
      let _oid

      // The response to sending a message contains the _oid in the root of the object but the message list in redux
      // contains the _oid in the model field so it needs to be moved to the model field
      if ('_oid' in messageFromApi) {
        _oid = messageFromApi._oid
        delete messageFromApi._oid
      }

      const messageToMerge = _oid
        ? { ...messageFromApi, model: { ...messageFromApi.model, _oid } }
        : messageFromApi

      const mergedMessage = mergeData(state.byId[messageId], {
        ...messageToMerge,
        sending: false,
        id: messageId,
      })

      const index = state.items.findIndex((item) => item.message_id === messageId)

      state.items[index] = mergedMessage
      state.byId[messageId] = mergedMessage
    })

    // Handle socket sent event
    builder.addCase(processSocketSentEvent, (state, action) => {
      const index = state.items.findIndex((item) => {
        return (
          item.model &&
          '_oid' in item.model &&
          item.model._oid &&
          'model' in action.payload &&
          action.payload.model &&
          '_oid' in action.payload.model &&
          action.payload.model._oid &&
          item.model._oid === action.payload.model._oid
        )
      })

      updateMessagesInState(state, index, action.payload)
    })

    // Handle socket received event
    builder.addCase(processSocketReceivedEvent, (state, action) => {
      const index = state.items.findIndex((item) => item.message_id === action.payload.message_id)

      updateMessagesInState(state, index, action.payload)
    })

    // Handle rejected message
    builder.addCase(sendMessage.rejected, (state, action) => {
      const { message } = action.meta.arg
      const index = state.items.findIndex((item) => item.message_id === message.message_id)

      const updatedItem = {
        ...state.items[index],
        sending: false,
        sendError: true,
      }

      state.items[index] = updatedItem
      state.byId[message.message_id] = updatedItem
    })
  },
})

export const {
  actions: messagesActions,
  selectors: messagesSelectors,
  reducer: messagesReducer,
  initialState: messagesInitialState,
} = messagesSlice

const updateMessagesInState = (
  state: ReturnType<typeof messagesReducer>,
  index: number,
  payload: IncomingMessage | OutgoingMessage,
) => {
  if (index < 0) {
    state.items.unshift(payload)
    state.byId[payload.message_id] = payload
  } else {
    const existingItem = state.items[index]
    const updatedPayload = { ...payload, id: existingItem.message_id }
    state.items[index] = updatedPayload
    state.byId[existingItem.message_id] = updatedPayload
  }
}

const processNotificationsQueue = (): IThunkAction => {
  return (dispatch, getState) => {
    if (!notificationsQueue.length) {
      return
    }

    const state = getState()
    const currentThreadId = getCurrentId(state)

    if (isSomeMessageSending(state)) {
      return
    }

    for (const notification of notificationsQueue) {
      if (notification.user_id === currentThreadId) {
        if (isOutgoingMessage(notification)) {
          dispatch(processSocketSentEvent(notification))
        }

        if (isIncomingMessage(notification)) {
          dispatch(processSocketReceivedEvent(notification))
        }
      }
    }

    notificationsQueue.length = 0
  }
}

function isOutgoingMessage(message: Message): message is OutgoingMessage {
  return !IN_TYPES.includes(message.type)
}

function isIncomingMessage(message: Message): message is IncomingMessage {
  return IN_TYPES.includes(message.type)
}

export const messageCreatedNotification = (data: Message): IThunkAction => {
  return (dispatch, getState) => {
    const state = getState()
    const currentThreadId = getCurrentId(state)
    if (data.user_id !== currentThreadId) {
      return
    }

    if (isOutgoingMessage(data)) {
      const isSending = isSomeMessageSending(state)
      if (isSending) {
        return notificationsQueue.push(data)
      }

      return dispatch(processSocketSentEvent(data))
    }
    // Message is not being sent out, so we can add it to the list

    return dispatch(processSocketReceivedEvent(data))
  }
}

anotherTabNotificationsListener.on(
  [WebSocketEvents.LC_SENT, WebSocketEvents.LC_ECHO],
  (data: MessageCreatedData, dispatch) => {
    if (!data.model) {
      return
    }
    dispatch(messageCreatedNotification(data.model))
  },
)

anotherTabNotificationsListener.on(
  WebSocketEvents.LC_RECEIVED,
  (data: MessageReceivedData, dispatch) => {
    if (!data.model) {
      return
    }
    dispatch(messageCreatedNotification(data.model))
  },
)

export const updateListItem = createAppAsyncThunk(
  `${NAMESPACE}/updateListItem`,
  async (item: Message, { dispatch, getState }) => {
    const state = getState()
    const hasItem = messagesSelectors.getItemById(state, item.message_id)

    if (hasItem) {
      return dispatch(messagesActions.updateItem({ id: item.message_id, data: item }))
    }

    return dispatch(messagesActions.prependItem({ item }))
  },
)

export const createListItem = createAppAsyncThunk(
  `${NAMESPACE}/createListItem`,
  async (item: Message, { dispatch }) => dispatch(messagesActions.prependItem({ item })),
)
